import type { ComponentType, ReactNode } from 'react';
import type { SkillManifest } from '@brandschutz/shared';

/**
 * Frontend-Skill-Registry (Plan §18.2/§18.3).
 *
 * Skills bringen UI-Komponenten in vordefinierte Slots (siehe KNOWN_SLOTS in
 * @brandschutz/shared). Die Registry hält pro Slot eine sortierte Liste an
 * Renderern; <SkillSlot name="..."> rendert sie in Reihenfolge.
 *
 * Bewusst minimal: keine Lazy-Loading-Magie, kein Plugin-Sandboxing.
 * Auto-Discovery erst, wenn wir > 5 Skills haben.
 */

/** Sortierwert, wenn ein Slot-Eintrag keinen angibt. */
export const DEFAULT_SLOT_ORDER = 100;

export interface SkillSlotEntry {
  /** Eindeutiger Key innerhalb des Slots (überschreibt frühere Einträge mit gleichem Key). */
  key: string;
  /** Sortierung niedriger zuerst — default DEFAULT_SLOT_ORDER. */
  order?: number;
  /** Was gerendert wird. Kann beliebige Props erhalten, die <SkillSlot> reicht durch. */
  render: ComponentType<Record<string, unknown>>;
}

export interface FrontendSkill {
  manifest: SkillManifest;
  /** Pro Slot-Name eine Liste an Renderern. */
  slots?: Partial<Record<string, SkillSlotEntry[]>>;
  /** Optionale Routes-Registrierung (Skill kann eigene React-Router-Routes liefern). */
  routes?: () => ReactNode;
}

class FrontendSkillRegistry {
  private readonly skills = new Map<string, FrontendSkill>();
  private readonly listeners = new Set<() => void>();

  register(skill: FrontendSkill): void {
    this.skills.set(skill.manifest.key, skill);
    this.listeners.forEach((l) => l());
  }

  list(): FrontendSkill[] {
    return Array.from(this.skills.values());
  }

  manifests(): SkillManifest[] {
    return this.list().map((s) => s.manifest);
  }

  /** Alle Slot-Einträge für einen Slot-Namen, sortiert (kleinster order zuerst). */
  entriesForSlot(slotName: string): SkillSlotEntry[] {
    const entries: SkillSlotEntry[] = [];
    for (const skill of this.skills.values()) {
      const slotEntries = skill.slots?.[slotName];
      if (slotEntries) entries.push(...slotEntries);
    }
    return entries.sort(
      (a, b) => (a.order ?? DEFAULT_SLOT_ORDER) - (b.order ?? DEFAULT_SLOT_ORDER),
    );
  }

  /** Listener für Re-Render, wenn Skills dazukommen (selten relevant — nur bei Hot-Reload). */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export const frontendSkillRegistry = new FrontendSkillRegistry();
